import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';
import { 
  BellIcon,
  CheckIcon,
  TrashIcon,
  FunnelIcon,
  ArrowPathIcon,
  EyeIcon,
  EyeSlashIcon
} from '@heroicons/react/24/outline';
import notificationService, { Notification, NotificationStats } from '../../services/notificationService';

// Regroupe les notifications par période pour une lecture organisée
const groupByPeriod = (notifs: Notification[]) => {
  const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const today = startOfDay(new Date());
  const yesterday = today - 86_400_000;
  const weekAgo = today - 6 * 86_400_000;

  const groups: { label: string; items: Notification[] }[] = [
    { label: "Aujourd'hui", items: [] },
    { label: 'Hier', items: [] },
    { label: 'Cette semaine', items: [] },
    { label: 'Plus ancien', items: [] },
  ];
  notifs.forEach((n) => {
    const t = startOfDay(new Date(n.created_at));
    if (t >= today) groups[0].items.push(n);
    else if (t >= yesterday) groups[1].items.push(n);
    else if (t >= weekAgo) groups[2].items.push(n);
    else groups[3].items.push(n);
  });
  return groups.filter((g) => g.items.length > 0);
};

const NotificationCenter: React.FC = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState<NotificationStats | null>(null);
  const [filter, setFilter] = useState<'all' | 'unread' | 'read'>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [priorityFilter, setPriorityFilter] = useState<string>('all');

  const loadNotifications = async () => {
    setLoading(true);
    try {
      const filters: any = {};
      
      if (filter === 'unread') filters.is_read = false;
      if (filter === 'read') filters.is_read = true;
      if (typeFilter !== 'all') filters.type = typeFilter;
      if (priorityFilter !== 'all') filters.priority = priorityFilter;

      const data = await notificationService.getNotifications(filters);
      setNotifications(data);
    } catch (error) {
      console.error('Erreur lors du chargement des notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const statsData = await notificationService.getStats();
      setStats(statsData);
    } catch (error) {
      console.error('Erreur lors du chargement des statistiques:', error);
    }
  };

  useEffect(() => {
    loadNotifications();
    loadStats();
  }, [filter, typeFilter, priorityFilter]);

  const handleMarkAsRead = async (notificationId: number) => {
    try {
      await notificationService.markAsRead(notificationId);
      setNotifications(prev => 
        prev.map(notif => 
          notif.id === notificationId 
            ? { ...notif, is_read: true } 
            : notif
        )
      );
      loadStats();
    } catch (error) {
      console.error('Erreur lors du marquage comme lu:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await notificationService.markAllAsRead();
      setNotifications(prev => 
        prev.map(notif => ({ ...notif, is_read: true }))
      );
      loadStats();
    } catch (error) {
      console.error('Erreur lors du marquage de toutes les notifications:', error);
    }
  };

  const handleClearRead = async () => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer toutes les notifications lues ?')) {
      try {
        await notificationService.clearReadNotifications();
        setNotifications(prev => prev.filter(notif => !notif.is_read));
        loadStats();
      } catch (error) {
        console.error('Erreur lors de la suppression:', error);
      }
    }
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'patient_created':
        return '👤';
      case 'consultation_created':
        return '🩺';
      case 'invoice_created':
        return '💰';
      case 'appointment_scheduled':
        return '📅';
      case 'stock_low':
        return '📦';
      case 'stock_empty':
        return '⚠️';
      case 'system_alert':
        return '🔔';
      default:
        return '📄';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return 'bg-red-500';
      case 'high':
        return 'bg-orange-500';
      case 'medium':
        return 'bg-blue-500';
      case 'low':
        return 'bg-gray-500';
      default:
        return 'bg-blue-500';
    }
  };

  const getPriorityBorder = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return 'border-l-red-500';
      case 'high':
        return 'border-l-orange-500';
      case 'medium':
        return 'border-l-blue-500';
      case 'low':
        return 'border-l-gray-500';
      default:
        return 'border-l-blue-500';
    }
  };

  const notificationTypes = [
    { value: 'all', label: 'Tous les types' },
    { value: 'patient_created', label: 'Nouveau patient' },
    { value: 'consultation_created', label: 'Nouvelle consultation' },
    { value: 'invoice_created', label: 'Nouvelle facture' },
    { value: 'appointment_scheduled', label: 'RDV programmé' },
    { value: 'stock_low', label: 'Stock faible' },
    { value: 'system_alert', label: 'Alerte système' }
  ];

  const priorityTypes = [
    { value: 'all', label: 'Toutes les priorités' },
    { value: 'urgent', label: 'Urgente' },
    { value: 'high', label: 'Élevée' },
    { value: 'medium', label: 'Moyenne' },
    { value: 'low', label: 'Faible' }
  ];

  return (
    <div className="max-w-6xl mx-auto">
      {/* En-tête */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <BellIcon className="h-8 w-8 text-blue-600" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Centre de notifications</h1>
              <p className="text-gray-600">Gérez toutes vos notifications en temps réel</p>
            </div>
          </div>
          
          <button
            onClick={loadNotifications}
            className="flex items-center space-x-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700"
          >
            <ArrowPathIcon className="h-4 w-4" />
            <span>Actualiser</span>
          </button>
        </div>

        {/* Statistiques */}
        {stats && (
          <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white p-4 rounded-lg shadow-sm border">
              <div className="flex items-center">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <BellIcon className="h-6 w-6 text-blue-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Total</p>
                  <p className="text-2xl font-semibold text-gray-900">{stats.total_count}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white p-4 rounded-lg shadow-sm border">
              <div className="flex items-center">
                <div className="p-2 bg-red-100 rounded-lg">
                  <EyeSlashIcon className="h-6 w-6 text-red-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Non lues</p>
                  <p className="text-2xl font-semibold text-red-600">{stats.unread_count}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white p-4 rounded-lg shadow-sm border">
              <div className="flex items-center">
                <div className="p-2 bg-green-100 rounded-lg">
                  <EyeIcon className="h-6 w-6 text-green-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Lues</p>
                  <p className="text-2xl font-semibold text-green-600">{stats.read_count}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white p-4 rounded-lg shadow-sm border">
              <div className="flex items-center">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <CheckIcon className="h-6 w-6 text-purple-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Taux de lecture</p>
                  <p className="text-2xl font-semibold text-purple-600">
                    {stats.total_count > 0 ? Math.round((stats.read_count / stats.total_count) * 100) : 0}%
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Filtres et actions */}
      <div className="bg-white p-4 rounded-lg shadow-sm border mb-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
          <div className="flex flex-col md:flex-row md:items-center space-y-2 md:space-y-0 md:space-x-4">
            {/* Filtre de statut */}
            <div className="flex items-center space-x-2">
              <FunnelIcon className="h-5 w-5 text-gray-400" />
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value as any)}
                className="border border-gray-200 rounded-xl px-3 py-2 focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              >
                <option value="all">Toutes</option>
                <option value="unread">Non lues</option>
                <option value="read">Lues</option>
              </select>
            </div>

            {/* Filtre de type */}
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="border border-gray-200 rounded-xl px-3 py-2 focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            >
              {notificationTypes.map(type => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>

            {/* Filtre de priorité */}
            <select
              value={priorityFilter}
              onChange={(e) => setPriorityFilter(e.target.value)}
              className="border border-gray-200 rounded-xl px-3 py-2 focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            >
              {priorityTypes.map(priority => (
                <option key={priority.value} value={priority.value}>{priority.label}</option>
              ))}
            </select>
          </div>

          {/* Actions */}
          <div className="flex items-center space-x-2">
            {stats && stats.unread_count > 0 && (
              <button
                onClick={handleMarkAllAsRead}
                className="flex items-center space-x-2 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm"
              >
                <CheckIcon className="h-4 w-4" />
                <span>Tout marquer comme lu</span>
              </button>
            )}
            
            {stats && stats.read_count > 0 && (
              <button
                onClick={handleClearRead}
                className="flex items-center space-x-2 px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm"
              >
                <TrashIcon className="h-4 w-4" />
                <span>Supprimer les lues</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Liste des notifications */}
      <div className="bg-white rounded-lg shadow-sm border">
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
            <p className="text-gray-500">Chargement des notifications...</p>
          </div>
        ) : notifications.length === 0 ? (
          <div className="p-8 text-center">
            <div className="text-6xl mb-4">🔔</div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Aucune notification</h3>
            <p className="text-gray-500">
              {filter === 'unread' 
                ? 'Aucune notification non lue' 
                : filter === 'read' 
                ? 'Aucune notification lue'
                : 'Aucune notification trouvée'}
            </p>
          </div>
        ) : (
          <div>
            {groupByPeriod(notifications).map((group) => (
              <div key={group.label}>
                <div className="sticky top-0 z-10 px-4 py-2 bg-gray-50 border-b border-gray-200 text-xs font-semibold uppercase tracking-wider text-gray-500">
                  {group.label}
                  <span className="ml-2 font-normal normal-case text-gray-400">
                    {group.items.length} notification{group.items.length > 1 ? 's' : ''}
                  </span>
                </div>
                <div className="divide-y divide-gray-200">
                  {group.items.map((notification) => (
                    <div
                      key={notification.id}
                      className={`p-4 hover:bg-gray-50 transition-colors cursor-pointer ${
                        !notification.is_read ? `bg-blue-50 border-l-4 ${getPriorityBorder(notification.priority)}` : ''
                      }`}
                      onClick={() => {
                        if (!notification.is_read) {
                          handleMarkAsRead(notification.id);
                        }
                        if (notification.action_url) {
                          navigate(notification.action_url);
                        }
                      }}
                    >
                      <div className="flex items-start space-x-4">
                        {/* Icône et priorité */}
                        <div className="flex-shrink-0">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center text-lg ${getPriorityColor(notification.priority)} text-white`}>
                            {getNotificationIcon(notification.notification_type)}
                          </div>
                        </div>

                        {/* Contenu */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <h4 className={`text-sm font-semibold ${!notification.is_read ? 'text-gray-900' : 'text-gray-700'}`}>
                                {notification.title}
                              </h4>

                              <p className="text-sm text-gray-600 mt-1">
                                {notification.message}
                              </p>

                              <div className="flex items-center space-x-4 mt-2 text-xs text-gray-500">
                                {notification.actor_name && (
                                  <span className="flex items-center">
                                    Créé par {notification.actor_name}
                                  </span>
                                )}

                                <span>
                                  {formatDistanceToNow(new Date(notification.created_at), {
                                    addSuffix: true,
                                    locale: fr
                                  })}
                                </span>

                                {notification.content_object_name && (
                                  <span className="bg-gray-100 px-2 py-1 rounded-full">
                                    {notification.content_object_name}
                                  </span>
                                )}
                              </div>
                            </div>

                            {/* Actions */}
                            <div className="flex items-center space-x-2">
                              {!notification.is_read && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleMarkAsRead(notification.id);
                                  }}
                                  className="p-1 text-blue-600 hover:text-blue-800 rounded"
                                  title="Marquer comme lu"
                                >
                                  <EyeIcon className="h-4 w-4" />
                                </button>
                              )}

                              {!notification.is_read && (
                                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationCenter;